import { SuretyAccountStatus } from "@inrev/common";
import { cn } from "@portal/components/lib/utils";
import { Icon } from "@portal/components/primitives";
import { getStatusCardByStatus } from "@portal/components/ui/StatusCard";
import { trackingAttrs } from "@portal/components/utils";
import { useOnClickOutside } from "@portal/hooks/useOnClickOutside";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type AccountStatusUpdate = Extract<SuretyAccountStatus, "review" | "closed" | "declined">;

type MenuPosition = { top: number; right: number; minWidth: number };

const MENU_OFFSET = 4;

type StatusTransitionOption = {
	value: AccountStatusUpdate;
	label: string;
};

type AdminAccountStatusTransitionDropdownProps = {
	currentStatus: SuretyAccountStatus;
	onStatusChange: (newStatus: SuretyAccountStatus) => void;
	canDecline?: boolean;
	isLoading?: boolean;
	className?: string;
	trackingPrefix?: string;
};

// Define allowed status transitions based on requirements
const getStatusTransitionOptions = (
	currentStatus: SuretyAccountStatus,
	canDecline: boolean,
): StatusTransitionOption[] => {
	const allStatuses: StatusTransitionOption[] = [
		{ value: "review", label: "In Review" },
		{ value: "declined", label: "Declined" },
		{ value: "closed", label: "Closed" },
	];

	// Approving still happens through the bonding-lines review flow; declining is also
	// available here so an approved or active account can be declined after the fact.
	const allowedTransitions: Record<SuretyAccountStatus, AccountStatusUpdate[]> = {
		review: ["closed"],
		active: ["closed", "declined"],
		declined: ["closed"],
		approved: ["closed", "declined"],
		closed: ["review"],
	};

	const allowedStatuses = allowedTransitions[currentStatus] || [];

	// Filter to only show allowed transitions (exclude current status)
	return allStatuses.filter(
		(status) =>
			allowedStatuses.includes(status.value) && (status.value !== "declined" || canDecline),
	);
};

export const AdminAccountStatusTransitionDropdown = ({
	currentStatus,
	onStatusChange,
	canDecline = true,
	isLoading = false,
	className,
	trackingPrefix,
}: AdminAccountStatusTransitionDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

	useOnClickOutside(menuRef, (event) => {
		if (event.target instanceof Node && triggerRef.current?.contains(event.target)) return;
		setIsOpen(false);
	});

	// The header is a collapsible overflow-hidden grid, so an in-flow menu is clipped at its
	// bottom edge. Portal the menu and pin it to the trigger's viewport rect instead.
	useLayoutEffect(() => {
		if (!isOpen) {
			setMenuPosition(null);
			return;
		}
		const trigger = triggerRef.current;
		if (!trigger) return;
		const rect = trigger.getBoundingClientRect();
		setMenuPosition({
			top: rect.bottom + MENU_OFFSET,
			right: window.innerWidth - rect.right,
			minWidth: rect.width,
		});
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const dismiss = (event: Event) => {
			if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
			setIsOpen(false);
		};
		window.addEventListener("scroll", dismiss, true);
		window.addEventListener("wheel", dismiss, true);
		window.addEventListener("resize", dismiss);
		return () => {
			window.removeEventListener("scroll", dismiss, true);
			window.removeEventListener("wheel", dismiss, true);
			window.removeEventListener("resize", dismiss);
		};
	}, [isOpen]);

	const handleStatusChange = (newStatus: AccountStatusUpdate) => {
		if (newStatus !== currentStatus) {
			onStatusChange(newStatus);
		}

		setIsOpen(false);
	};

	const options = getStatusTransitionOptions(currentStatus, canDecline);
	const statusChevron = (
		<span className="flex w-9 shrink-0 items-center justify-center">
			<Icon
				type="chevron-down"
				size="mini"
				className={cn("shrink-0 transition-transform duration-200", isOpen && "rotate-180")}
			/>
		</span>
	);

	// Don't show dropdown if no transitions are allowed
	const hasTransitions = options.length > 0;
	if (!hasTransitions) {
		return (
			<div className={className}>
				{getStatusCardByStatus(currentStatus, "w-full min-w-0 justify-between")}
			</div>
		);
	}

	return (
		<div ref={triggerRef} className={cn("h-fit w-full", className)}>
			<button
				{...trackingAttrs(trackingPrefix ? `${trackingPrefix}-menu` : undefined)}
				onClick={() => setIsOpen(!isOpen)}
				disabled={isLoading}
				className={cn(
					"flex w-full items-center justify-between border-none bg-transparent p-0 cursor-pointer",
					isLoading && "cursor-not-allowed opacity-50",
				)}
			>
				{getStatusCardByStatus(currentStatus, "w-full min-w-0 justify-between !pr-0", {
					trailing: statusChevron,
				})}
			</button>

			{isOpen &&
				menuPosition &&
				createPortal(
					<div
						ref={menuRef}
						data-testid="account-status-transition-menu"
						className="fixed z-[9999] flex w-max flex-col gap-1 rounded-md border border-bc-default bg-surface-base p-2 shadow-lg"
						style={{
							top: menuPosition.top,
							right: menuPosition.right,
							minWidth: menuPosition.minWidth,
						}}
					>
						{options.map((option) => (
							<button
								key={option.value}
								{...trackingAttrs(trackingPrefix ? `${trackingPrefix}-${option.value}` : undefined)}
								onClick={() => handleStatusChange(option.value)}
								disabled={isLoading}
								className="w-full rounded-md hover:bg-surface-light"
							>
								{getStatusCardByStatus(option.value, "w-full min-w-max", {
									truncateLabel: false,
								})}
							</button>
						))}
					</div>,
					document.body,
				)}
		</div>
	);
};
